'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';

import { CAPTCHA_FRAME_PATH, CAPTCHA_MESSAGE_TYPE, CAPTCHA_ORIGIN } from '@/constants/captcha-constants';

import { cn } from '@cineamo/ui/src/helpers/cn';

import { useIsMounted } from '@cineamo/ui/src/hooks/useIsMounted';

type TurnstileWidgetProps = {
    /** Called with the verification token once solved, and with `null` on expiry, error, or reset. */
    onVerify: (token: string | null) => void;
    className?: string;
};

type CaptchaMessage = { type?: unknown; token?: unknown; error?: unknown } | null;

const TURNSTILE_SITE_KEY = process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;

/**
 * Cloudflare Turnstile managed widget (CIN-13378 spam protection for cinema-website forms), framed
 * from captcha.<CIN_DOMAIN> so one widget hostname covers every cinema domain (CIN-15667, see
 * `src/constants/captcha-constants.ts`). The frame posts its tokens back to this window.
 * A token is single-use, so forms change this component's `key` after every submit attempt.
 * Renders nothing when NEXT_PUBLIC_TURNSTILE_SITE_KEY isn't set, so local/dev and any
 * deployment without a configured Turnstile site keep working without captcha handling.
 */
export default function TurnstileWidget({ onVerify, className }: TurnstileWidgetProps) {
    const t = useTranslations();
    const isMounted = useIsMounted();
    const frameRef = useRef<HTMLIFrameElement>(null);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        const onMessage = (event: MessageEvent<CaptchaMessage>) => {
            if (
                event.origin !== CAPTCHA_ORIGIN ||
                event.source !== frameRef.current?.contentWindow ||
                event.data?.type !== CAPTCHA_MESSAGE_TYPE
            ) {
                return;
            }
            setHasError(event.data.error === true);
            onVerify(typeof event.data.token === 'string' ? event.data.token : null);
        };

        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, [onVerify]);

    if (!TURNSTILE_SITE_KEY) {
        return null;
    }

    return (
        <div className={cn('mb-8', className)}>
            {/* The src needs this page's origin, so it is set after hydration only. */}
            <iframe
                ref={frameRef}
                src={
                    isMounted
                        ? `${CAPTCHA_ORIGIN}${CAPTCHA_FRAME_PATH}?origin=${encodeURIComponent(window.location.origin)}`
                        : undefined
                }
                title={t('common.captcha-title')}
                width={300}
                height={65}
                className="block border-none"
            />
            {hasError && <p className="text-body-12 text-red mt-2">{t('common.captcha-error')}</p>}
        </div>
    );
}
